/**
 * Bộ biểu tượng 7w Chat — NGUỒN DUY NHẤT cho web (`Icon.tsx`), trang xem trước, file SVG xuất ra (`npm run icons`)
 * và mobile sau này (react-native-svg).
 *
 * Quy cách: lưới 24×24, vùng vẽ 2–22, nét 1.75 (`ICON_STROKE`), đầu/nối nét bo tròn, không tô (trừ chấm nhỏ có
 * `fill`). Mỗi biểu tượng là danh sách phần tử SVG dạng dữ liệu (không phải chuỗi HTML) → React dựng bằng
 * `createElement`, không cần innerHTML (tương thích CSP Trusted Types).
 *
 * File thuần dữ liệu, chỉ dùng cú pháp TypeScript xóa được (Node chạy trực tiếp bằng type stripping).
 */

export type IconElement = readonly ['path' | 'circle' | 'rect' | 'line', Readonly<Record<string, string | number>>];

export const ICON_STROKE = 1.75;

const dot = (cx: number, cy: number, r = 1.1): IconElement => ['circle', { cx, cy, r, fill: 'currentColor', stroke: 'none' }];
const p = (d: string): IconElement => ['path', { d }];

export const ICONS = {
  // ------------------------------------------------------------- Điều hướng
  chat: [
    p('M21 11.5c0 4.42-4.03 8-9 8-1.3 0-2.54-.25-3.66-.69L3 20l1.4-4.2A7.6 7.6 0 0 1 3 11.5c0-4.42 4.03-8 9-8s9 3.58 9 8Z'),
    p('M8 10h8M8 13.5h5'),
  ],
  grid: [
    ['rect', { x: 3, y: 3, width: 7.5, height: 7.5, rx: 2 }],
    ['rect', { x: 13.5, y: 3, width: 7.5, height: 7.5, rx: 2 }],
    ['rect', { x: 3, y: 13.5, width: 7.5, height: 7.5, rx: 2 }],
    ['rect', { x: 13.5, y: 13.5, width: 7.5, height: 7.5, rx: 2 }],
  ],
  task: [
    ['rect', { x: 5, y: 4, width: 14, height: 17.5, rx: 2.5 }],
    p('M9 4V3a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v1'),
    p('m9 13 2.2 2.2 4-4.7'),
  ],
  approval: [
    p('M11 21H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h7l5 5v3'),
    p('M14 3v5h5'),
    ['circle', { cx: 17, cy: 17, r: 4.25 }],
    p('m15.2 17.1 1.3 1.3 2.4-2.6'),
  ],
  bot: [
    ['rect', { x: 4, y: 7, width: 16, height: 13, rx: 4 }],
    p('M12 7V4'),
    dot(12, 3, 1.25),
    p('M9 12.5V14M15 12.5V14M9.5 17h5'),
    p('M2 12v3M22 12v3'),
  ],
  chart: [p('M3 21h18'), p('M6.5 17v-5M11 17V6.5M15.5 17v-8M20 17v-3.5')],
  people: [
    ['circle', { cx: 9, cy: 8, r: 3.5 }],
    p('M2.5 20.5c.6-3.5 3.3-6 6.5-6s5.9 2.5 6.5 6'),
    p('M16 4.6a3.5 3.5 0 0 1 0 6.8M18 14.7c2 .8 3.3 2.9 3.6 5.8'),
  ],
  user: [['circle', { cx: 12, cy: 8, r: 4 }], p('M4 21c.8-4 4-7 8-7s7.2 3 8 7')],

  // ------------------------------------------------------------- Thao tác
  plus: [p('M12 5v14M5 12h14')],
  minus: [p('M5 12h14')],
  search: [['circle', { cx: 11, cy: 11, r: 7 }], p('m20.5 20.5-4.5-4.5')],
  close: [p('M6 6l12 12M18 6 6 18')],
  back: [p('m15 18-6-6 6-6')],
  chevron: [p('m9 6 6 6-6 6')],
  down: [p('m6 9 6 6 6-6')],
  up: [p('m18 15-6-6-6 6')],
  more: [dot(5, 12), dot(12, 12), dot(19, 12)],
  'more-vertical': [dot(12, 5), dot(12, 12), dot(12, 19)],
  send: [p('M21.5 2.5 10.5 13.5'), p('m21.5 2.5-7 19-4-8-8-4 19-7Z')],
  clip: [p('m20.5 11.5-8.4 8.4a5.4 5.4 0 0 1-7.6-7.6l8.4-8.4a3.6 3.6 0 0 1 5.1 5.1l-8.4 8.4a1.8 1.8 0 0 1-2.5-2.5l7.8-7.8')],
  smile: [['circle', { cx: 12, cy: 12, r: 9 }], p('M8.5 14.5a4.5 4.5 0 0 0 7 0'), dot(9, 9.75, 1), dot(15, 9.75, 1)],
  reply: [p('M9 7 4 12l5 5'), p('M4 12h10a6 6 0 0 1 6 6v1')],
  forward: [p('m15 7 5 5-5 5'), p('M20 12H10a6 6 0 0 0-6 6v1')],
  edit: [p('M4 20h4L19.4 8.6a2.1 2.1 0 0 0-3-3L5 17v3Z'), p('m14.5 7.5 3 3')],
  trash: [p('M4 7h16M9.5 7V4.5h5V7'), p('M6 7l1 13h10l1-13'), p('M10 11v5.5M14 11v5.5')],
  copy: [['rect', { x: 8, y: 8, width: 13, height: 13, rx: 2 }], p('M16 8V5a1 1 0 0 0-1-1H4a1 1 0 0 0-1 1v11a1 1 0 0 0 1 1h4')],
  download: [p('M12 3.5v12'), p('m7 10.5 5 5 5-5'), p('M4.5 20.5h15')],
  upload: [p('M12 15.5v-12'), p('m7 8.5 5-5 5 5'), p('M4.5 20.5h15')],
  external: [p('M14 4h6v6M20 4l-9 9'), p('M18 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h5')],
  filter: [p('M4 5h16l-6.2 7.6V19l-3.6 2v-8.4L4 5Z')],
  settings: [p('M4 7h9.5M18.5 7H20M4 17h3.5M12.5 17H20'), ['circle', { cx: 16, cy: 7, r: 2.5 }], ['circle', { cx: 10, cy: 17, r: 2.5 }]],
  bell: [p('M6 9.5a6 6 0 0 1 12 0c0 5.5 2.5 7.5 2.5 7.5h-17S6 15 6 9.5Z'), p('M10 20.5a2.3 2.3 0 0 0 4 0')],
  'bell-off': [p('M8.6 4.2A6 6 0 0 1 18 9.5c0 2.6.6 4.4 1.2 5.6M6.1 7.9A6 6 0 0 0 6 9.5C6 15 3.5 17 3.5 17H17'), p('M10 20.5a2.3 2.3 0 0 0 4 0'), p('M3 3l18 18')],
  pin: [p('M9 3h6'), p('M10 3v5.5L7 13h10l-3-4.5V3'), p('M12 13v8')],
  unpin: [p('M9 3h6M14 8.5V3M10 3v1.5'), p('M7 13h6M17 13l-1.3-1.9'), p('M12 13v8'), p('M3 3l18 18')],
  thread: [
    p('M14.5 9a5 5 0 0 1-5 5H5.8L3 16.2V9a5 5 0 0 1 5-5h1.5a5 5 0 0 1 5 5Z'),
    p('M17.5 9.5h.5a3 3 0 0 1 3 3v6.3l-2.2-1.8H15a3 3 0 0 1-2.8-2'),
  ],
  panel: [['rect', { x: 3, y: 3, width: 18, height: 18, rx: 3 }], p('M15 3v18')],
  logout: [p('M9.5 20.5H5.5a2 2 0 0 1-2-2v-13a2 2 0 0 1 2-2h4'), p('m16 16.5 4.5-4.5L16 7.5M20.5 12H9.5')],
  refresh: [p('M20 11a8 8 0 0 0-14.6-4.4L4 8.5M4 3.5v5h5'), p('M4 13a8 8 0 0 0 14.6 4.4l1.4-1.9M20 20.5v-5h-5')],

  // ------------------------------------------------------------- Trạng thái & bảo mật
  check: [p('m4.5 12.5 5 5 10-11')],
  'check-double': [p('m2.5 13 4 4 9-10'), p('m11.5 15.5 1.5 1.5 9-10')],
  clock: [['circle', { cx: 12, cy: 12, r: 9 }], p('M12 7v5l3.2 2')],
  lock: [['rect', { x: 5, y: 10.5, width: 14, height: 10.5, rx: 2.5 }], p('M8 10.5V7a4 4 0 0 1 8 0v3.5'), p('M12 15v2')],
  unlock: [['rect', { x: 5, y: 10.5, width: 14, height: 10.5, rx: 2.5 }], p('M8 10.5V7a4 4 0 0 1 7.7-1.5'), p('M12 15v2')],
  shield: [p('M12 3 4.5 6v6c0 4.6 3.2 7.7 7.5 9 4.3-1.3 7.5-4.4 7.5-9V6L12 3Z'), p('m9 12 2.2 2.2 4-4.4')],
  key: [['circle', { cx: 8, cy: 15.5, r: 4.5 }], p('m11.3 12.2 9.2-9.2M17.5 6l2.5 2.5M15 8.5l2 2')],
  warning: [p('M10.3 4.3 2.6 17.5A2 2 0 0 0 4.3 20.5h15.4a2 2 0 0 0 1.7-3L13.7 4.3a2 2 0 0 0-3.4 0Z'), p('M12 9.5v4.5'), dot(12, 17, 1)],
  info: [['circle', { cx: 12, cy: 12, r: 9 }], p('M12 11v5.5'), dot(12, 7.75, 1)],
  error: [['circle', { cx: 12, cy: 12, r: 9 }], p('m9 9 6 6M15 9l-6 6')],
  eye: [p('M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12Z'), ['circle', { cx: 12, cy: 12, r: 3 }]],
  'eye-off': [p('M10.6 5.6A9.4 9.4 0 0 1 12 5.5c6 0 9.5 6.5 9.5 6.5a17 17 0 0 1-2.2 3M6.6 6.6C3.9 8.4 2.5 12 2.5 12S6 18.5 12 18.5c1.9 0 3.6-.6 5-1.5'), p('M9.9 9.9a3 3 0 0 0 4.2 4.2'), p('M3 3l18 18')],
  device: [['rect', { x: 6, y: 2.5, width: 12, height: 19, rx: 3 }], p('M10.5 18h3')],
  desktop: [['rect', { x: 2.5, y: 4, width: 19, height: 13, rx: 2 }], p('M8 21h8M12 17v4')],
  moon: [p('M20 14.5A8 8 0 1 1 9.5 4a6.5 6.5 0 0 0 10.5 10.5Z')],
  sun: [['circle', { cx: 12, cy: 12, r: 4 }], p('M12 2.5v2M12 19.5v2M2.5 12h2M19.5 12h2M5.3 5.3l1.4 1.4M17.3 17.3l1.4 1.4M5.3 18.7l1.4-1.4M17.3 6.7l1.4-1.4')],
  star: [p('m12 3 2.8 5.7 6.2.9-4.5 4.4 1.1 6.2L12 17.3 6.4 20.2l1.1-6.2L3 9.6l6.2-.9L12 3Z')],

  // ------------------------------------------------------------- Nội dung
  file: [p('M14 2.5H6.5a2 2 0 0 0-2 2v15a2 2 0 0 0 2 2h11a2 2 0 0 0 2-2V8l-5.5-5.5Z'), p('M14 2.5V8h5.5'), p('M8.5 13h7M8.5 16.5h4.5')],
  image: [['rect', { x: 3, y: 3.5, width: 18, height: 17, rx: 2.5 }], ['circle', { cx: 8.5, cy: 9, r: 1.75 }], p('m3.5 17 5-5 4 4 3-3 5 5')],
  video: [['rect', { x: 2.5, y: 6, width: 13.5, height: 12, rx: 2.5 }], p('m16 10.5 5.5-3.2v9.4L16 13.5')],
  call: [p('M5 3.5h3.2l1.6 4-2 1.3a11 11 0 0 0 5.4 5.4l1.3-2 4 1.6v3.2a2 2 0 0 1-2 2A14.5 14.5 0 0 1 3 5.5a2 2 0 0 1 2-2Z')],
  'mic-off': [p('M9 5.5V5a3 3 0 0 1 6 0v6a3 3 0 0 1-.4 1.5M5.5 11a6.5 6.5 0 0 0 10.3 5.2M12 17.5v4'), p('M3 3l18 18')],
  'video-off': [p('M16 10.5l5.5-3.2v9.4L16 13.5M10.5 6H13a3 3 0 0 1 3 3v6M3 8.5V15a3 3 0 0 0 3 3h8'), p('M3 3l18 18')],
  mic: [['rect', { x: 9, y: 2.5, width: 6, height: 11.5, rx: 3 }], p('M5.5 11a6.5 6.5 0 0 0 13 0M12 17.5v4')],
  link: [p('M10 13.5a4.5 4.5 0 0 0 6.4.4l3-3a4.5 4.5 0 0 0-6.4-6.4l-1.3 1.3'), p('M14 10.5a4.5 4.5 0 0 0-6.4-.4l-3 3a4.5 4.5 0 0 0 6.4 6.4l1.3-1.3')],
  hash: [p('M4.5 9h16M3.5 15h16M10 3.5 8 20.5M16 3.5l-2 17')],
  at: [['circle', { cx: 12, cy: 12, r: 4 }], p('M16 8v5a2.8 2.8 0 0 0 5.5 0v-1A9.5 9.5 0 1 0 17.8 19.6')],
  calendar: [['rect', { x: 3, y: 4.5, width: 18, height: 16.5, rx: 2.5 }], p('M3 9.5h18M8 2.5v4M16 2.5v4'), dot(8, 13.5, 1), dot(12, 13.5, 1), dot(16, 13.5, 1), dot(8, 17, 1), dot(12, 17, 1)],
  folder: [p('M3 7a2 2 0 0 1 2-2h4.2l2 2.2H19a2 2 0 0 1 2 2V18a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7Z')],
  location: [p('M12 21.5s-7-6.1-7-11.5a7 7 0 0 1 14 0c0 5.4-7 11.5-7 11.5Z'), ['circle', { cx: 12, cy: 10, r: 2.5 }]],
  music: [p('M9 18V5l11-2v13'), ['circle', { cx: 6.5, cy: 18, r: 2.5 }], ['circle', { cx: 17.5, cy: 16, r: 2.5 }]],
  play: [p('M7 4.5v15l12-7.5-12-7.5Z')],
  pause: [p('M8 4.5v15M16 4.5v15')],
  quote: [p('M5 5v14'), p('M10 8h9M10 12h9M10 16h5')],
  code: [p('m8 7-5 5 5 5M16 7l5 5-5 5M14 4l-4 16')],
  format: [p('M5 19 11 5h2l6 14'), p('M7.5 14h9')],
  project: [['rect', { x: 3, y: 7, width: 18, height: 13.5, rx: 2.5 }], p('M8.5 7V5a2 2 0 0 1 2-2h3a2 2 0 0 1 2 2v2'), p('M3 12.5h18'), p('M10.5 12.5v1.5h3v-1.5')],
} as const satisfies Record<string, readonly IconElement[]>;

export type IconName = keyof typeof ICONS;

/** Tên ngữ nghĩa → biểu tượng (giữ tên cũ trong code; tên mới cho W3). */
export const ICON_ALIASES = {
  portal: 'grid',
  tasks: 'task',
  agent: 'bot',
  management: 'chart',
  attach: 'clip',
  emoji: 'smile',
  settings2: 'settings',
  read: 'check-double',
  security: 'shield',
  phone: 'device',
  document: 'file',
} as const satisfies Record<string, IconName>;

export type IconAlias = keyof typeof ICON_ALIASES;

export function resolveIcon(name: string): readonly IconElement[] | undefined {
  // Object.hasOwn: tránh khớp nhầm khóa của prototype (vd. "toString").
  const key = Object.hasOwn(ICONS, name) ? name : Object.hasOwn(ICON_ALIASES, name) ? ICON_ALIASES[name as IconAlias] : undefined;
  return key ? ICONS[key as IconName] : undefined;
}

/** Nhóm cho trang xem trước. */
export const ICON_GROUPS: readonly { title: string; names: readonly IconName[] }[] = [
  { title: 'Điều hướng', names: ['chat', 'grid', 'task', 'approval', 'bot', 'chart', 'people', 'user'] },
  {
    title: 'Thao tác',
    names: ['plus', 'minus', 'search', 'close', 'back', 'chevron', 'down', 'up', 'more', 'more-vertical', 'send', 'clip', 'smile', 'reply', 'forward', 'edit', 'trash', 'copy', 'download', 'upload', 'external', 'filter', 'settings', 'bell', 'bell-off', 'pin', 'unpin', 'thread', 'panel', 'logout', 'refresh'],
  },
  {
    title: 'Trạng thái & bảo mật',
    names: ['check', 'check-double', 'clock', 'lock', 'unlock', 'shield', 'key', 'warning', 'info', 'error', 'eye', 'eye-off', 'device', 'desktop', 'moon', 'sun', 'star'],
  },
  { title: 'Nội dung', names: ['file', 'image', 'video', 'mic', 'mic-off', 'video-off', 'call', 'link', 'hash', 'at', 'calendar', 'folder', 'project', 'location', 'music', 'play', 'pause', 'quote', 'code', 'format'] },
];

// ------------------------------------------------------------- Logo 7w (viewBox 512×512)

/** Màu logo: nền chéo accent (#3346ff) → navy thanh điều hướng; "7" màu brand-mark như logo chữ trên NavRail. */
export const BRAND = {
  gradient: ['#3346ff', '#142e55'] as const,
  seven: '#21c4ef',
  w: '#ffffff',
};

/** Nét "7" và "w" của logo (viewBox 512×512, nét bo tròn) — favicon, icon PWA. */
export const LOGO_SEVEN = 'M112 166h132l-72 184';
export const LOGO_W = 'M266 234l36 116 38-92 38 92 36-116';
export const LOGO_STROKE_WIDTH = 48;
